import React, { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, Lock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { useAuth } from "../../context/AuthContext";
import { errorMessage } from "../../lib/api";
import { studio } from "../../mock";

const GoogleMark = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
    <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.2 1.3-1.7 3.9-5.5 3.9-3.3 0-6-2.7-6-6.1s2.7-6.1 6-6.1c1.9 0 3.1.8 3.9 1.5l2.6-2.6C16.9 3.2 14.7 2.2 12 2.2 6.6 2.2 2.2 6.6 2.2 12S6.6 21.8 12 21.8c5.6 0 9.4-4 9.4-9.6 0-.6-.1-1.1-.2-1.6H12z" />
  </svg>
);

const AdminLogin = () => {
  const { login, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ username: "", password: "" });
  const [busy, setBusy] = useState(false);

  if (!loading && isAuthenticated) return <Navigate to={location.state?.from || "/admin"} replace />;

  const signInWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = `${window.location.origin}/admin`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.username || !form.password) {
      toast.error("Enter your username and password");
      return;
    }
    setBusy(true);
    try {
      await login(form.username.trim(), form.password);
      toast.success("Welcome back");
      navigate(location.state?.from || "/admin", { replace: true });
    } catch (err) {
      toast.error(errorMessage(err, "Login failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-5" data-testid="admin-login-page">
      <div className="absolute inset-0">
        <img src={studio.images.hero} alt="" className="h-full w-full object-cover opacity-30 grayscale" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-background/40" />
      </div>
      <div className="relative w-full max-w-md border border-border bg-card/90 p-8 backdrop-blur md:p-10">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center border border-foreground/30">
            <span className="display text-2xl leading-none">A</span>
          </span>
          <div className="leading-none">
            <p className="display text-2xl tracking-wide">AVALON</p>
            <p className="text-[9px] font-semibold uppercase tracking-[0.32em] text-muted-foreground">Admin</p>
          </div>
        </div>
        <h1 className="display mt-8 text-5xl uppercase leading-none tracking-wide">Sign in</h1>
        <p className="mt-2 text-sm text-muted-foreground">Landing page builder, submissions &amp; promo tracking.</p>

        <Button
          type="button"
          variant="outline"
          onClick={signInWithGoogle}
          className="mt-8 h-12 w-full rounded-none border-foreground/30 bg-transparent text-[11px] font-semibold uppercase tracking-[0.2em] hover:border-foreground hover:bg-foreground hover:text-background"
          data-testid="login-google"
        >
          <GoogleMark /> <span className="ml-3">Continue with Google</span>
        </Button>
        <p className="mt-2 text-[11px] text-muted-foreground">For the master admin and invited team members.</p>

        <div className="my-7 flex items-center gap-4 text-[10px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">
          <span className="h-px flex-1 bg-border" /> or use a password <span className="h-px flex-1 bg-border" />
        </div>

        <form onSubmit={submit} className="space-y-5" data-testid="admin-login-form">
          <div className="space-y-2">
            <Label htmlFor="username" className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Username</Label>
            <Input
              id="username"
              autoComplete="username"
              value={form.username}
              onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
              className="h-12 rounded-none border-border bg-transparent px-4 focus-visible:ring-1 focus-visible:ring-primary focus-visible:ring-offset-0"
              data-testid="login-username"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password" className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Password</Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              className="h-12 rounded-none border-border bg-transparent px-4 focus-visible:ring-1 focus-visible:ring-primary focus-visible:ring-offset-0"
              data-testid="login-password"
            />
          </div>
          <Button type="submit" disabled={busy} className="h-12 w-full rounded-none text-[11px] font-semibold uppercase tracking-[0.2em]" data-testid="login-submit">
            {busy ? "Signing in…" : "Sign in"} {!busy && <ArrowRight className="ml-2 h-4 w-4" />}
          </Button>
        </form>
        <p className="mt-6 flex items-center gap-2 text-[11px] text-muted-foreground">
          <Lock className="h-3 w-3" /> Restricted to Avalon staff.
        </p>
      </div>
    </main>
  );
};

export default AdminLogin;
